/*
 * workspace-patterns.ts
 * ─────────────────────
 * Runtime controller for the family-specific workspace layouts rendered by
 * Views/Tools/_WP_*.cshtml partials. Provides:
 *
 *   1. Region discovery — finds [data-wp-region="<id>"] elements
 *   2. Template fragment routing — routes [data-wp-target="<id>"] fragments
 *      from the tool's runtime template into the matching named region
 *   3. Split-handle drag resize — pointer-based resize of adjacent regions
 *   4. Drop-zone activation — for UploadCanvas pattern (Image tools)
 *   5. Global API — window.ToolWorkspacePatterns for new-tool-public-page.js
 *
 * Backward compatibility: if the tool's htmlTemplate has no [data-wp-target]
 * fragments, no routing happens. The template still injects into
 * [data-tool-content-host] as before — workspace regions remain empty/decorative.
 */
const MIN_REGION_SIZE_PX = 200;
const SPLIT_PROPORTION_STORAGE_PREFIX = 'tn:wp:split:';
let activeConfig = null;
/** Discover all data-wp-region elements inside a wrapper. */
function discoverRegions(wrapper) {
    const map = new Map();
    const elements = wrapper.querySelectorAll('[data-wp-region]');
    elements.forEach(el => {
        const id = el.dataset['wpRegion'];
        if (id)
            map.set(id, el);
    });
    return map;
}
/**
 * Route fragments tagged with [data-wp-target="<region>"] from the runtime
 * template into the matching named region. Returns true if any fragment was
 * routed (so the caller can skip the fallback content-host injection).
 */
function routeTemplateFragments(template, regions) {
    const fragments = template.querySelectorAll('[data-wp-target]');
    if (fragments.length === 0)
        return false;
    let routed = 0;
    fragments.forEach(fragment => {
        const target = fragment.dataset['wpTarget'];
        if (target && regions.has(target)) {
            const region = regions.get(target);
            if (region) {
                region.appendChild(fragment);
                routed += 1;
            }
        }
    });
    return routed > 0;
}
/** Read a previously-saved split proportion for this pattern, if any. */
function readStoredProportion(pattern) {
    try {
        const raw = window.localStorage.getItem(SPLIT_PROPORTION_STORAGE_PREFIX + pattern);
        if (!raw)
            return null;
        const value = parseFloat(raw);
        if (!Number.isFinite(value) || value <= 0 || value >= 1)
            return null;
        return value;
    }
    catch {
        return null;
    }
}
/** Persist a split proportion (clamped 0..1). */
function writeStoredProportion(pattern, proportion) {
    try {
        const clamped = Math.min(0.9, Math.max(0.1, proportion));
        window.localStorage.setItem(SPLIT_PROPORTION_STORAGE_PREFIX + pattern, clamped.toFixed(4));
    }
    catch {
        /* ignore quota / privacy errors */
    }
}
/** Get the immediate-sibling region elements on either side of the split handle. */
function getSplitNeighbors(handle) {
    let before = handle.previousElementSibling;
    while (before && !before.matches('[data-wp-region]')) {
        before = before.previousElementSibling;
    }
    let after = handle.nextElementSibling;
    while (after && !after.matches('[data-wp-region]')) {
        after = after.nextElementSibling;
    }
    return { before, after };
}
/** Initialise pointer-based drag-to-resize on the split handle. */
function initSplitResize(handle, wrapper, pattern) {
    const isHorizontal = handle.hasAttribute('data-wp-split-horizontal');
    const { before, after } = getSplitNeighbors(handle);
    if (!before || !after)
        return;
    // Restore stored proportion on init.
    const stored = readStoredProportion(pattern);
    if (stored !== null) {
        applyProportion(before, after, stored);
    }
    let active = false;
    let containerSize = 0;
    let startCoord = 0;
    let startBeforeSize = 0;
    function applyProportion(b, a, prop) {
        // Use flex-grow to express the proportion; clear flex-basis hints.
        const beforeFlex = prop;
        const afterFlex = 1 - prop;
        b.style.flex = `${beforeFlex} 1 0`;
        a.style.flex = `${afterFlex} 1 0`;
    }
    function onPointerDown(event) {
        active = true;
        handle.setAttribute('data-wp-active', 'true');
        handle.setPointerCapture(event.pointerId);
        const rect = wrapper.getBoundingClientRect();
        containerSize = isHorizontal ? rect.height : rect.width;
        startCoord = isHorizontal ? event.clientY : event.clientX;
        const beforeRect = before.getBoundingClientRect();
        startBeforeSize = isHorizontal ? beforeRect.height : beforeRect.width;
        event.preventDefault();
    }
    function onPointerMove(event) {
        if (!active)
            return;
        const current = isHorizontal ? event.clientY : event.clientX;
        const delta = current - startCoord;
        const newBeforeSize = startBeforeSize + delta;
        const minSize = MIN_REGION_SIZE_PX;
        if (newBeforeSize < minSize || newBeforeSize > containerSize - minSize)
            return;
        const proportion = newBeforeSize / containerSize;
        applyProportion(before, after, proportion);
    }
    function onPointerUp(event) {
        if (!active)
            return;
        active = false;
        handle.removeAttribute('data-wp-active');
        try {
            handle.releasePointerCapture(event.pointerId);
        }
        catch { /* noop */ }
        // Compute and persist final proportion.
        const beforeRect = before.getBoundingClientRect();
        const finalSize = isHorizontal ? beforeRect.height : beforeRect.width;
        if (containerSize > 0) {
            writeStoredProportion(pattern, finalSize / containerSize);
        }
    }
    function onKeyDown(event) {
        // Keyboard accessibility: arrow keys nudge the split by 5%.
        const stepKeys = isHorizontal
            ? { dec: 'ArrowUp', inc: 'ArrowDown' }
            : { dec: 'ArrowLeft', inc: 'ArrowRight' };
        if (event.key !== stepKeys.dec && event.key !== stepKeys.inc)
            return;
        const beforeRect = before.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const total = isHorizontal ? wrapperRect.height : wrapperRect.width;
        const current = isHorizontal ? beforeRect.height : beforeRect.width;
        if (total <= 0)
            return;
        const stepPx = total * 0.05;
        const next = current + (event.key === stepKeys.inc ? stepPx : -stepPx);
        const minSize = MIN_REGION_SIZE_PX;
        if (next < minSize || next > total - minSize)
            return;
        const proportion = next / total;
        applyProportion(before, after, proportion);
        writeStoredProportion(pattern, proportion);
        event.preventDefault();
    }
    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerUp);
    handle.addEventListener('pointercancel', onPointerUp);
    handle.addEventListener('keydown', onKeyDown);
}
/** Wire drop-zone visual feedback for UploadCanvas pattern. */
function initDropZones(wrapper) {
    const zones = wrapper.querySelectorAll('[data-wp-dropzone="true"]');
    if (zones.length === 0)
        return;
    zones.forEach(zone => {
        let dragDepth = 0;
        zone.addEventListener('dragenter', (event) => {
            event.preventDefault();
            dragDepth += 1;
            zone.setAttribute('data-wp-dropzone-active', 'true');
        });
        zone.addEventListener('dragleave', () => {
            dragDepth = Math.max(0, dragDepth - 1);
            if (dragDepth === 0) {
                zone.removeAttribute('data-wp-dropzone-active');
            }
        });
        zone.addEventListener('dragover', (event) => {
            event.preventDefault();
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'copy';
            }
        });
        zone.addEventListener('drop', () => {
            dragDepth = 0;
            zone.removeAttribute('data-wp-dropzone-active');
            // The actual file handling is delegated to the tool template's
            // own drop listeners; we just clean up the visual state.
        });
    });
}
/**
 * Initialise the workspace pattern. Called automatically on DOMContentLoaded
 * and re-callable via window.ToolWorkspacePatterns.init() if the host changes.
 */
function init(host) {
    const root = host ?? document.querySelector('[data-tool-content-host]');
    if (!root)
        return null;
    const wrapper = root.querySelector('[data-workspace-pattern]');
    if (!wrapper)
        return null;
    const pattern = wrapper.dataset['workspacePattern'] ?? 'split-editor';
    const regions = discoverRegions(wrapper);
    const splitHandle = wrapper.querySelector('[data-wp-split-handle]');
    if (splitHandle) {
        initSplitResize(splitHandle, wrapper, pattern);
    }
    initDropZones(wrapper);
    activeConfig = { pattern, wrapper, regions, splitHandle };
    // Watch for runtime template handoff: when the tool template arrives,
    // attempt to route any [data-wp-target] fragments into the regions.
    observeTemplateHandoff(root, regions);
    return activeConfig;
}
/**
 * Hide the workspace pattern wrapper + skeleton when a rich, self-contained
 * tool template has mounted. A template counts as "self-contained" when it
 * carries the `.tool-template-root` class AND is NOT a generic loading stub
 * (`.tool-generic-template`). Self-contained templates render their own
 * input / output / run-zone markup; the workspace-pattern regions and
 * skeleton would otherwise duplicate that markup and clash visually.
 *
 * Generic stub templates (json-formatter today) still benefit from the
 * workspace pattern region renderer, so we leave the wrapper visible in
 * that case.
 */
function hideWorkspaceChromeIfSelfContained(host) {
    const template = host.querySelector('.tool-template-root:not(.tool-generic-template), [data-tool-template-root]:not(.tool-generic-template)');
    if (!template)
        return false;
    // Hide the workspace pattern wrapper (input/output regions provided by
    // _WP_*.cshtml partials) — the tool template owns this surface now.
    host.querySelectorAll('[data-workspace-pattern]').forEach((node) => {
        if (node !== template && !template.contains(node)) {
            node.hidden = true;
            node.setAttribute('aria-hidden', 'true');
        }
    });
    // Hide the loading skeleton — content has arrived.
    host.querySelectorAll('[data-tool-chrome-slot="skeleton"]').forEach((node) => {
        node.hidden = true;
        node.setAttribute('aria-hidden', 'true');
    });
    return true;
}
/**
 * Observe the runtime template handoff slot. When the tool runtime injects
 * its template, either route any [data-wp-target] fragments into the
 * pattern regions OR (if the template is self-contained) hide the
 * pattern wrapper + skeleton to give the template full control.
 */
function observeTemplateHandoff(host, regions) {
    let handled = false;
    const tryHandle = () => {
        if (handled)
            return true;
        // First try to route [data-wp-target] fragments — when the tool
        // template opts into the workspace regions.
        if (routeTemplateFragments(host, regions)) {
            handled = true;
            return true;
        }
        // Otherwise, if a self-contained template has landed, hide the chrome.
        if (hideWorkspaceChromeIfSelfContained(host)) {
            handled = true;
            return true;
        }
        return false;
    };
    // Initial pass in case the template is already present (cached / SSR).
    if (tryHandle())
        return;
    const observer = new MutationObserver(() => {
        if (tryHandle()) {
            observer.disconnect();
        }
    });
    observer.observe(host, {
        subtree: true,
        childList: true
    });
    // Stop listening after a generous timeout so we don't leak observers
    // on tools that never use workspace regions.
    window.setTimeout(() => observer.disconnect(), 30000);
}
const api = {
    init,
    routeFragments: routeTemplateFragments,
    getActive: () => activeConfig
};
window.ToolWorkspacePatterns = api;
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
}
else {
    init();
}
export {};
//# sourceMappingURL=workspace-patterns.js.map